const WORD_PROGRESS_KEY = 'color-dots:words:v1';

export interface WordLevelResult {
  correct: number;
  total: number;
  mistakes: number;
  newWords: number;
  at: string;
}

export interface TopicProgress {
  completed: Record<string, WordLevelResult>;
  lastLevel: string;
  mistakeWords: string[];
  totalSessions: number;
}

export interface WordProgress {
  version: number;
  topics: Record<string, TopicProgress>;
  lastTopic: string;
}

const EMPTY: WordProgress = {
  version: 1,
  topics: {},
  lastTopic: 'fruits-vegetables',
};

function emptyTopic(): TopicProgress {
  return {
    completed: {},
    lastLevel: 'W-01',
    mistakeWords: [],
    totalSessions: 0,
  };
}

export function loadWordProgress(): WordProgress {
  try {
    const raw = localStorage.getItem(WORD_PROGRESS_KEY);
    if (!raw) return { ...EMPTY, topics: {} };
    const parsed = JSON.parse(raw) as Partial<WordProgress>;
    return {
      version: 1,
      topics: parsed.topics ?? {},
      lastTopic: typeof parsed.lastTopic === 'string' ? parsed.lastTopic : 'fruits-vegetables',
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveWordProgress(progress: WordProgress): void {
  localStorage.setItem(WORD_PROGRESS_KEY, JSON.stringify(progress));
}

export function getTopicProgress(topicId: string): TopicProgress {
  const progress = loadWordProgress();
  return progress.topics[topicId] ?? emptyTopic();
}

export function markWordLevelCompleted(
  topicId: string,
  levelId: string,
  result: Omit<WordLevelResult, 'at'>,
  mistakeWordIds: string[] = [],
): void {
  const progress = loadWordProgress();
  const topic = progress.topics[topicId] ?? emptyTopic();

  topic.completed[levelId] = {
    ...result,
    at: new Date().toISOString(),
  };
  topic.lastLevel = levelId;
  topic.totalSessions += 1;

  const mistakeSet = new Set(topic.mistakeWords);
  for (const id of mistakeWordIds) {
    mistakeSet.add(id);
  }
  topic.mistakeWords = [...mistakeSet];

  progress.topics[topicId] = topic;
  progress.lastTopic = topicId;
  saveWordProgress(progress);
}

export function setLastWordLevel(topicId: string, levelId: string): void {
  const progress = loadWordProgress();
  const topic = progress.topics[topicId] ?? emptyTopic();
  topic.lastLevel = levelId;
  progress.topics[topicId] = topic;
  progress.lastTopic = topicId;
  saveWordProgress(progress);
}

export function getCompletedWordLevelIds(topicId: string): Set<string> {
  return new Set(Object.keys(getTopicProgress(topicId).completed));
}

export function getTopicCompletionCount(topicId: string): number {
  return Object.keys(getTopicProgress(topicId).completed).length;
}

export function setLastWordTopic(topicId: string): void {
  const progress = loadWordProgress();
  progress.lastTopic = topicId;
  saveWordProgress(progress);
}
